// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React from "react";
import i18n from "i18next";
import {
  DeleteOutlined,
  EyeOutlined,
  StopOutlined,
  LinkOutlined,
  CheckOutlined
} from "@ant-design/icons";
import { navigate } from "@reach/router";
import { alignRight, orderDescend } from "../../../../utils/Constant";
import {
  BranchListDto,
  NewsInfoResponseDTO
} from "../../../../api/backend-openapi";
import TableButton from "../../../components/TableButton";
import ModalButton from "../../../components/ModalButton";
import {
  requestDeleteNews,
  requestDuplicateNews,
  requestWorkflowUnpublishNews
} from "../../../../state/news/NewsEffects";
import NewsPreviewButton from "../details/preview/NewsPreviewButton";
import { CopyOutlined, MoreOutlined } from "@ant-design/icons/lib";
import { Button, message, Popover } from "antd";
import { showConfirm } from "../../../components/ConfirmModal";
import { NewsWorkflowStatus } from "../../../types/NewsTypes";
import NewsStatusTag from "../details/NewsStatusTag";
import {
  CustomTooltip,
  tooltipDateRender,
  tooltipRender
} from "../../../components/CustomTooltip";
import NewsPriorityTag from "../details/NewsPriorityTag";
import { NewsThemeStore } from "../../../../state/newsTheme/NewsThemeStore";
import {
  canBeDeleted,
  getPublicationDateFrom,
  getStatusFrom
} from "../../../../utils/NewsUtils";
const popoverContent = (record: NewsInfoResponseDTO, canDelete: boolean) => (
  <div className="d-inline-flex">
    <TableButton
      icon={<EyeOutlined />}
      tooltipTitle={i18n.t("news_list.table.tooltip.view")}
      onClick={() => {
        navigate(`/news/${record.id}`);
      }}
      buttonClassName="btn-primary mr-1 btn-list"
    />
    <TableButton
      icon={<CopyOutlined />}
      tooltipTitle={i18n.t("news_list.table.tooltip.duplicate")}
      onClick={() => {
        showConfirm(
          i18n.t("news_list.table.confirmation.duplicate", {
            news: record.title
          }),
          () => {
            requestDuplicateNews({
              id: record.id
            });
          }
        );
      }}
      buttonClassName="btn-primary mr-1 btn-list"
    />
    {record.status === NewsWorkflowStatus.PUBLISHED && (
      <TableButton
        icon={<StopOutlined />}
        buttonClassName="btn-secondary mr-1 btn-list"
        onClick={() =>
          showConfirm(
            i18n.t("news_list.table.confirmation.unpublish", {
              news: record.title
            }),
            (reason?: string) => {
              requestWorkflowUnpublishNews({
                id: record.id,
                dto: {
                  isPublish: false,
                  publishNotes: reason
                }
              });
            },
            undefined,
            (valueToTranslate: string) => i18n.t(valueToTranslate),
            "unpublish"
          )
        }
        tooltipTitle={i18n.t("news_list.table.tooltip.unpublish")}
      />
    )}
    <NewsPreviewButton
      newsId={record.id}
      iconButton={true}
      newsThemeId={record.newsThemeId}
    />
    <ModalButton
      modalTitle={i18n.t("news_list.table.deep_link")}
      modalBody={record.deepLink}
      icon={<LinkOutlined />}
      tooltipTitle={i18n.t("news_list.table.tooltip.deep_link")}
      buttonClassName="btn-tertiary mr-1 btn-list"
      footer={[
        <Button
          key="copy"
          className="btn-primary"
          onClick={() => {
            navigator.clipboard.writeText(record.deepLink);
            message.success(i18n.t("edit_news.deeplink_copied"));
          }}
        >
          <CopyOutlined /> {i18n.t("edit_news.copy")}
        </Button>
      ]}
    />
    {canBeDeleted(record) && canDelete && (
      <TableButton
        icon={<DeleteOutlined />}
        buttonClassName="btn-danger btn-list"
        onClick={() =>
          showConfirm(
            i18n.t("news_list.table.confirmation.delete", {
              news: record.title
            }),
            () => {
              requestDeleteNews(record);
            }
          )
        }
        tooltipTitle={i18n.t("news_list.table.tooltip.delete")}
      />
    )}
  </div>
);

export const columns = (canDelete: boolean, branchList: BranchListDto) => [
  {
    title: i18n.t("news_list.table.title"),
    key: "title",
    dataIndex: "title",
    ellipsis: false,
    render: tooltipRender,
    defaultSortOrder: undefined,
    sorter: true
  },
  {
    title: i18n.t("news_list.table.branch"),
    key: "branch",
    width: "7%",
    ellipsis: false,
    render: (record: NewsInfoResponseDTO) => {
      if (
        record &&
        record.newsThemeId &&
        NewsThemeStore.getState().length > 0
      ) {
        const newsThemes = NewsThemeStore.getState().filter(
          newsTheme => newsTheme.id === record.newsThemeId
        );
        if (!!newsThemes && newsThemes.length === 1) {
          const branch = newsThemes[0].branch;
          return tooltipRender(branchList[branch]?.label || "");
        }
      }
      return "";
    },
    defaultFilteredValue: undefined,
    sorter: true
  },
  {
    title: i18n.t("news_list.table.news_theme"),
    key: "newsTheme",
    width: "12%",
    ellipsis: false,
    render: (record: NewsInfoResponseDTO) => {
      return record &&
        record.newsThemeId &&
        NewsThemeStore.getState().length > 0 ? (
        <CustomTooltip
          data={
            NewsThemeStore.getState().filter(
              newsTheme => newsTheme.id === record.newsThemeId
            )[0]
              ? NewsThemeStore.getState().filter(
                  newsTheme => newsTheme.id === record.newsThemeId
                )[0].name
              : ""
          }
        />
      ) : (
        ""
      );
    },
    defaultSortOrder: undefined,
    sorter: true
  },
  {
    title: i18n.t("news.status"),
    key: "status",
    width: "12%",
    ellipsis: {
      showTitle: false
    },
    defaultSortOrder: undefined,
    sorter: true,
    render: (record: NewsInfoResponseDTO) => {
      return record && <NewsStatusTag status={getStatusFrom(record)} />;
    }
  },
  {
    title: i18n.t("news.priority"),
    key: "priority",
    dataIndex: "priority",
    width: "7%",
    ellipsis: true,
    render: (priority: number) => {
      return <NewsPriorityTag priority={priority} />;
    },
    defaultSortOrder: undefined,
    sorter: true
  },
  {
    title: i18n.t("news_list.table.news_publication_date"),
    key: "publicationDate",
    width: "10%",
    ellipsis: false,
    defaultSortOrder: undefined,
    sorter: true,
    render: (record: NewsInfoResponseDTO) => {
      return tooltipDateRender(getPublicationDateFrom(record));
    }
  },
  {
    title: i18n.t("news_list.table.news_creation_date"),
    key: "creationDate",
    dataIndex: "creationDate",
    width: "10%",
    ellipsis: false,
    defaultSortOrder: orderDescend,
    sorter: true,
    render: tooltipDateRender
  },
  {
    title: i18n.t("news.archived"),
    key: "archived",
    width: "7%",
    dataIndex: "archived",
    ellipsis: true,
    render: (archived: boolean) => {
      return archived ? <CheckOutlined /> : <></>;
    }
  },
  {
    title: i18n.t("news_list.table.action"),
    key: "action",
    fixed: alignRight,
    width: "8%",
    onCell: () => {
      return {
        onClick: (e: any) => e.stopPropagation()
      };
    },
    render: (text: string, record: NewsInfoResponseDTO) => (
      <Popover
        content={popoverContent(record, canDelete)}
        placement="bottomRight"
        trigger="hover"
      >
        <Button
          icon={<MoreOutlined />}
          type="text"
          size="large"
          className="btn-list"
        />
      </Popover>
    )
  }
];
